import { gql, type TypedDocumentNode } from '@apollo/client';
import type { ReactNode } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import {
  So5LeaderboardSeasonality,
  So5LeaderboardType,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Flame } from '@sorare/core/src/atoms/icons/Flame';
import { Lineup } from '@sorare/core/src/atoms/icons/Lineup';
import { Market } from '@sorare/core/src/atoms/icons/Market';
import { iconExternalLink } from '@sorare/core/src/atoms/icons/system/iconExternalLink.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelXS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType, withFragments } from '@sorare/core/src/lib/gql';

import { BenchExtraUsedIn } from '#src/components/compose/BenchExtra/BenchExtraUsedIn/index.tsx';
import { getComposeTeamRoute } from '#src/lib/so5.ts';

import type {
  getCardUsage_cardUsageInterface,
  getCardUsage_cardUsageInterface_UsedInSo5Lineups_,
} from './__generated__/index.graphql.ts';

type So5Leaderboard = NonNullable<
  getCardUsage_cardUsageInterface_UsedInSo5Lineups_['so5Lineups'][number]['so5Leaderboard']
>;

type CardUsage =
  | { kind: 'LISTED_ON_MARKET' }
  | { kind: 'MISSION' }
  | { kind: 'PRE_SET_LINEUPS'; count: number }
  | { kind: 'PRO_LINEUP'; stepLevel: number | null }
  | { kind: 'SO5_LINEUP'; so5LineupId: string; so5Leaderboard: So5Leaderboard };

type Scope = { so5LineupId?: string; taskId?: string };

export const getCardUsage = withFragments(
  (
    usedIn: getCardUsage_cardUsageInterface | null | undefined,
    { so5LineupId, taskId }: Scope = {}
  ): CardUsage | null => {
    if (isType(usedIn, 'ListedOnMarket')) {
      return { kind: 'LISTED_ON_MARKET' };
    }

    if (isType(usedIn, 'UsedInSo5Lineups')) {
      const so5Lineup = usedIn.so5Lineups.find(({ id }) => id !== so5LineupId);

      return so5Lineup?.so5Leaderboard
        ? {
            kind: 'SO5_LINEUP',
            so5LineupId: so5Lineup.id,
            so5Leaderboard: so5Lineup.so5Leaderboard,
          }
        : null;
    }

    if (isType(usedIn, 'UsedInTaskLineups')) {
      const taskLineup = usedIn.taskLineups.at(0);

      return taskLineup
        ? { kind: 'PRO_LINEUP', stepLevel: taskLineup.step?.level ?? null }
        : null;
    }

    if (isType(usedIn, 'UsedInTasks')) {
      const task = usedIn.tasks.find(({ id }) => id !== taskId);
      if (!task) {
        return null;
      }

      return isType(task, 'ThresholdPickerTask')
        ? { kind: 'PRO_LINEUP', stepLevel: null }
        : { kind: 'MISSION' };
    }

    if (isType(usedIn, 'UsedInTaskPreparedLineups')) {
      return {
        kind: 'PRE_SET_LINEUPS',
        count: usedIn.taskPreparedLineups.length,
      };
    }

    return null;
  },
  {
    cardUsageInterface: gql`
      fragment getCardUsage_cardUsageInterface on CardUsageInterface {
        slug
        ... on UsedInSo5Lineups {
          slug
          so5Lineups {
            id
            so5Leaderboard {
              slug
              seasonality
              so5Fixture {
                slug
                type
              }
              shortDisplayName: displayName(short: true, withSeasonality: false)
              so5TournamentType {
                id
                so5LeaderboardType
              }
              so5League {
                slug
                shortDisplayName
              }
              so5LeaderboardGroup {
                slug
                displayName
              }
            }
          }
        }
        ... on UsedInTaskLineups {
          slug
          taskLineups {
            id
            step {
              id
              level
            }
          }
        }
        ... on UsedInTasks {
          slug
          tasks {
            id
          }
        }
        ... on UsedInTaskPreparedLineups {
          slug
          taskPreparedLineups {
            id
          }
        }
      }
    ` as TypedDocumentNode<getCardUsage_cardUsageInterface>,
  }
);

const messages = defineMessages({
  editLineup: {
    defaultMessage: 'Edit lineup',
  },
});

type ContentProps = {
  icon: ReactNode;
  iconOnly?: boolean;
  children: ReactNode;
};
const Content = ({ icon, iconOnly, children }: ContentProps) => (
  <Horizontal gap={0.5} center>
    {icon}
    {!iconOnly && (
      <LabelXS>
        <Ellipsis>{children}</Ellipsis>
      </LabelXS>
    )}
  </Horizontal>
);

const So5LineupStrip = ({
  so5LineupId,
  so5Leaderboard,
  iconOnly,
  withLineupLink,
}: {
  so5LineupId: string;
  so5Leaderboard: So5Leaderboard;
  iconOnly?: boolean;
  withLineupLink?: boolean;
}) => {
  const { sport } = useSportContext();
  const { formatMessage } = useIntl();
  const { so5League, so5LeaderboardGroup, so5TournamentType } = so5Leaderboard;

  const arenaColor =
    so5Leaderboard.seasonality === So5LeaderboardSeasonality.IN_SEASON
      ? 'var(--c-modes-s27-arena-secondary)'
      : 'var(--c-modes-classic-arena-secondary)';

  const so5LeagueName =
    so5TournamentType.so5LeaderboardType === So5LeaderboardType.SPECIAL_WEEKLY
      ? 'Special Event'
      : so5League?.shortDisplayName;
  const leaderboardName =
    sport === Sport.FOOTBALL
      ? so5LeagueName || so5LeaderboardGroup?.displayName
      : so5Leaderboard.shortDisplayName;

  const editLineupRoute = getComposeTeamRoute({
    leaderboardSlug: so5Leaderboard.slug,
    lineupId: so5LineupId,
    sport,
    eventType: so5Leaderboard.so5Fixture.type.toLowerCase(),
  });

  return (
    <Horizontal gap={0.5} center>
      <BenchExtraUsedIn color={arenaColor} iconOnly={iconOnly}>
        {leaderboardName ? (
          <FormattedMessage
            defaultMessage="Used in {name}"
            values={{ name: leaderboardName }}
          />
        ) : (
          <FormattedMessage defaultMessage="Used in Lineup" />
        )}
      </BenchExtraUsedIn>
      {!!withLineupLink && !iconOnly && (
        <ButtonBase
          to={editLineupRoute}
          externalLink
          aria-label={formatMessage(messages.editLineup)}
        >
          <FontAwesomeIcon icon={iconExternalLink} size="xs" />
        </ButtonBase>
      )}
    </Horizontal>
  );
};

type Props = {
  usage: CardUsage;
  iconOnly?: boolean;
  withLineupLink?: boolean;
};

export const CardUsageStrip = ({ usage, iconOnly, withLineupLink }: Props) => {
  if (usage.kind === 'LISTED_ON_MARKET') {
    return (
      <Content icon={<Market active size={1.5} />} iconOnly={iconOnly}>
        <FormattedMessage {...fantasy.listedCard} />
      </Content>
    );
  }

  if (usage.kind === 'SO5_LINEUP') {
    return (
      <So5LineupStrip
        so5LineupId={usage.so5LineupId}
        so5Leaderboard={usage.so5Leaderboard}
        iconOnly={iconOnly}
        withLineupLink={withLineupLink}
      />
    );
  }

  if (usage.kind === 'PRO_LINEUP') {
    return (
      <Content
        icon={<Flame size={1.5} color="var(--c-modes-pro-tertiary)" />}
        iconOnly={iconOnly}
      >
        {usage.stepLevel === null ? (
          <FormattedMessage defaultMessage="In Pro" />
        ) : (
          <FormattedMessage
            defaultMessage="In Pro Step {level}"
            values={{ level: usage.stepLevel + 1 }}
          />
        )}
      </Content>
    );
  }

  if (usage.kind === 'MISSION') {
    return (
      <Content icon={<Flame size={1.5} />} iconOnly={iconOnly}>
        <FormattedMessage defaultMessage="Used in mission" />
      </Content>
    );
  }

  return (
    <Content
      icon={<Lineup size={1.5} color="var(--c-nd-600)" />}
      iconOnly={iconOnly}
    >
      <FormattedMessage
        defaultMessage="In Pre-set ({count})"
        values={{ count: usage.count }}
      />
    </Content>
  );
};
